import clsx from 'clsx';
import { type ReactNode } from 'react';

import { Button, type ButtonProps } from '../Button/Button';
import styles from './EmptyState.module.css';
import { MotifPlaceholder } from './MotifPlaceholder';

/**
 * Configuration for an EmptyState action button.
 *
 * `label` and `onClick` are the core fields. Any additional V2 Button props
 * (e.g. `disabled`, `loading`, `leftSection`) may be passed through and are
 * forwarded to the underlying <Button>. `variant`/`size` are controlled
 * internally and cannot be overridden.
 */
export type EmptyStateAction = Omit<
  ButtonProps,
  'variant' | 'size' | 'children' | 'onClick'
> & {
  /** Button label */
  label: ReactNode;
  /** Click handler */
  onClick?: () => void;
};

export interface EmptyStateProps {
  /** Title text. Omit to hide. */
  title?: ReactNode;
  /** Subtitle text. Omit to hide. */
  subtitle?: ReactNode;
  /**
   * Custom image node. When omitted, a default placeholder box with the
   * Figma motif glyph is rendered. Pass `null` to render no image at all.
   */
  image?: ReactNode;
  /** Primary action — rendered as a primary Button on the right. */
  mainAction?: EmptyStateAction;
  /** Secondary action — rendered as a secondary Button on the left. */
  secondaryAction?: EmptyStateAction;
  className?: string;
}

const renderAction = (
  { label, ...rest }: EmptyStateAction,
  variant: 'primary' | 'secondary'
) => (
  <Button variant={variant} size="sm" {...rest}>
    {label}
  </Button>
);

export const EmptyState = ({
  title,
  subtitle,
  image,
  mainAction,
  secondaryAction,
  className,
}: EmptyStateProps) => {
  const hasText = title !== undefined || subtitle !== undefined;
  const hasActions = Boolean(mainAction || secondaryAction);

  return (
    <div className={clsx(styles.root, className)}>
      <div className={styles.content}>
        {image !== undefined ? (
          image
        ) : (
          <div className={styles.imageBox}>
            <MotifPlaceholder />
          </div>
        )}
        {hasText && (
          <div className={styles.text}>
            {title !== undefined && <p className={styles.title}>{title}</p>}
            {subtitle !== undefined && (
              <p className={styles.subtitle}>{subtitle}</p>
            )}
          </div>
        )}
      </div>
      {hasActions && (
        <div className={styles.actions}>
          {secondaryAction && renderAction(secondaryAction, 'secondary')}
          {mainAction && renderAction(mainAction, 'primary')}
        </div>
      )}
    </div>
  );
};

EmptyState.displayName = 'EmptyState';
